import { useState, type ReactNode } from 'react';
import { Check, Pencil, X } from 'lucide-react';

interface Props {
  icon: ReactNode;
  title: string;
  description: string;
  value: string;
  hint?: string;
  onSave: (value: string) => Promise<string | null>;
}

const MAX_LENGTH = 1000;

export default function ManagerAssistantMessageCard({ icon, title, description, value, hint, onSave }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function startEditing() {
    setDraft(value);
    setError(null);
    setEditing(true);
  }

  async function save() {
    setSaving(true);
    setError(null);
    const failure = await onSave(draft.trim());
    setSaving(false);
    if (failure) setError(failure);
    else setEditing(false);
  }

  return (
    <div className="flex flex-col rounded-xl border border-slate-200 bg-white p-5 transition hover:border-emerald-200">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">{icon} {title}</div>
        {!editing && (
          <button onClick={startEditing} className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-medium text-emerald-700 transition hover:bg-emerald-50">
            <Pencil className="h-3.5 w-3.5" /> Editar
          </button>
        )}
      </div>
      <p className="mb-3 text-xs leading-5 text-slate-500">{description}</p>

      {editing ? (
        <>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={8}
            maxLength={MAX_LENGTH}
            className="w-full resize-y rounded-lg border border-slate-300 p-3 text-sm leading-6 text-slate-800 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
          />
          <div className="mt-1 flex justify-between text-xs text-slate-400">
            <span>{hint}</span>
            <span>{draft.length}/{MAX_LENGTH}</span>
          </div>
          {error && <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm leading-5 text-red-700">{error}</p>}
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => setEditing(false)} disabled={saving} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-60">
              <X className="h-4 w-4" /> Cancelar
            </button>
            <button onClick={() => void save()} disabled={saving || !draft.trim()} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60">
              <Check className="h-4 w-4" /> {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </>
      ) : (
        <p className="whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-sm leading-6 text-slate-600">{value}</p>
      )}
    </div>
  );
}
